import { BusinessObjectBase } from './base';
import { organizationPropertySchema } from '@schemas/business-objects/organization-property';
import type { OrganizationPropertyValue } from './organization-property-value';
import type { Id, OrganizationPropertyType } from '../base-types';

export interface OrganizationPropertyData {
  readonly id: Id;
  readonly name?: string;
  readonly type?: OrganizationPropertyType;
  readonly validValues?: string;
  readonly sequence?: number;
  readonly organizationPropertyValues?: OrganizationPropertyValue[];
}

export interface OrganizationProperty extends Readonly<OrganizationPropertyData> {}
export class OrganizationProperty extends BusinessObjectBase {
  static readonly entityName = 'OrganizationProperty' as const;
  static readonly plural = 'organization-properties';
  static schema = organizationPropertySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<OrganizationProperty[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new OrganizationProperty(data));
  }

  static async findOne(filter?: any): Promise<OrganizationProperty | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new OrganizationProperty(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<OrganizationProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new OrganizationProperty(data);
  }

  static async create(
    data: Omit<OrganizationPropertyData, 'id'>,
  ): Promise<OrganizationProperty> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new OrganizationProperty(result);
  }

  static async createMany(
    data: Omit<OrganizationPropertyData, 'id'>[],
  ): Promise<OrganizationProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new OrganizationProperty(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<OrganizationPropertyData>,
  ): Promise<OrganizationProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new OrganizationProperty(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
